import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { isPayoutAddressFormatValid, type PayoutAddressDraft } from './payout-address';
import { LIVE_PAYOUT_ADDRESS_KINDS, PAYOUT_ADDRESS_KINDS, type PayoutAddressKind } from './types';

type Props = {
  values: PayoutAddressDraft;
  onChange: (kind: PayoutAddressKind, value: string) => void;
  /** Keeps input ids unique when two dialogs live on the same page. */
  idPrefix: string;
  isDisabled?: boolean;
};

/**
 * One input per chain family, shared by the platform wallets dialog and the
 * merchant payout dialog. Only the EVM format is checked here; the hint under
 * each field says what crypto-api will accept so a typo is caught before the
 * round trip. A family that is not live yet is shown read-only, "coming soon".
 */
export function PayoutAddressFields({ values, onChange, idPrefix, isDisabled }: Props) {
  const { t } = useTranslation('manageCryptoPayments');

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {PAYOUT_ADDRESS_KINDS.map((kind) => {
        const value = values[kind];
        const isInvalid = !isPayoutAddressFormatValid(kind, value);
        const inputId = `${idPrefix}-${kind}`;
        const isComingSoon = !LIVE_PAYOUT_ADDRESS_KINDS.includes(kind);
        return (
          <div key={kind} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label htmlFor={inputId}>{t(`chains.${kind}`)}</Label>
              {isComingSoon && (
                <Badge variant="secondary" className="text-xs">
                  {t('addressFields.comingSoon')}
                </Badge>
              )}
            </div>
            <Input
              id={inputId}
              value={value}
              onChange={(event) => onChange(kind, event.target.value)}
              className="font-mono text-sm"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={isInvalid || undefined}
              disabled={isDisabled || isComingSoon}
            />
            <p className={isInvalid ? 'text-destructive text-xs' : 'text-muted-foreground text-xs'}>
              {isInvalid ? t('addressFields.invalidEvm') : t(`addressFields.hints.${kind}`)}
            </p>
          </div>
        );
      })}
    </div>
  );
}
